import React, { useState, useEffect } from 'react'
import './ApplyV2EducationStep.scss'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import {
  usePostEducationV2ApplyMutation,
  useDeleteEducationV2ApplyMutation,
  usePutEducationV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'

import { EducationIcon } from './icons/icon'
import { TrashIcon } from './icons/trash_icon'
import { PencilIcon } from './icons/pencil_icon'
import { LocationIcon } from './icons/location_icon'
import { CalendarIcon } from './icons/calendar_icon'

const months = [
  '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'
]
const years = Array.from({ length: 70 }, (_, i) => (new Date().getFullYear() - i).toString())

type Education = {
  uid?: string
  institution: string
  degree: string
  fieldOfStudy: string
  startMonth: string
  startYear: string
  endMonth: string
  endYear: string
  location: string
  description: string
}

const defaultEducation: Education = {
  institution: '',
  degree: '',
  fieldOfStudy: '',
  startMonth: '',
  startYear: '',
  endMonth: '',
  endYear: '',
  location: '',
  description: '',
}

const mapApiEducation = (apiEdu: any): Education => ({
  uid: apiEdu.uid || '',
  institution: apiEdu.schoolName || '',
  degree: apiEdu.degree || '',
  fieldOfStudy: '',
  startMonth: apiEdu.fromDate ? apiEdu.fromDate.split('-')[1] : '',
  startYear: apiEdu.fromDate ? apiEdu.fromDate.split('-')[0] : '',
  endMonth: apiEdu.toDate ? apiEdu.toDate.split('-')[1] : '',
  endYear: apiEdu.toDate ? apiEdu.toDate.split('-')[0] : '',
  location: apiEdu.location || '',
  description: apiEdu.description || '',
});

const EducationStep: React.FC = () => {
  const { initData, settingsData } = useApplyV2Context()
  const [postEducation, { isLoading: isPostLoading }] = usePostEducationV2ApplyMutation()
  const [putEducation, { isLoading: isPutLoading }] = usePutEducationV2ApplyMutation()
  const [deleteEducation, { isLoading: isDeleteLoading }] = useDeleteEducationV2ApplyMutation()


  console.log('fucking initdata')
  console.log(initData)

  const [educations, setEducations] = useState<Education[]>(() => initData?.data?.education || [])

  const [state, setState] = useState<'empty' | 'list' | 'add' | 'edit'>(
    (initData?.data?.education?.length ?? 0) === 0 ? 'empty' : 'list'
  )
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [form, setForm] = useState<Education>(defaultEducation)
  const [noEducation, setNoEducation] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const uid = initData?.data?.uid

  // Мапимо поля з форми у payload для API
  const educationToApiBody = (data: Education) => ({
    ...(data.uid ? { uid: data.uid } : {}),
    degree: data.degree,
    toDate: `${data.endYear}-${data.endMonth || '01'}-01`,
    fromDate: `${data.startYear}-${data.startMonth || '01'}-01`,
    location: data.location,
    schoolName: data.institution,
    description: data.description,
    // educationType, educationListId — якщо потрібно
  })

  // Додати або оновити освіту
  const handleFormSubmit = async () => {
    if (!uid) return

    try {
      if (editIndex !== null && educations[editIndex]?.uid) {
        // ОНОВЛЕННЯ (PUT)
        const editing = educations[editIndex]
        const res = await putEducation({
          uid,
          body: { ...educationToApiBody(form), uid: editing.uid! },
        }).unwrap()

        // Оновлюємо локальний стейт (онлайн по відповіді або просто мапою)
        setEducations(prev =>
          prev.map((ed, i) => (i === editIndex ? { ...form, uid: editing.uid } : ed))
        )
      } else {
        // ДОДАВАННЯ (POST)
        const res = await postEducation({
          uid,
          body: educationToApiBody(form),
        }).unwrap()

        // uid повертається з бекенду у res.data.uid (чи як воно приходить)
        setEducations(prev => [...prev, { ...form, uid: res?.data?.uid || '' }])
      }

      setState('list')
      setEditIndex(null)
      setForm(defaultEducation)

      setFieldErrors({}); // очищаємо помилки після успіху

    } catch (e: any) {
      // ----- Ось тут витягуємо помилки -----
      const errFields: { [key: string]: string } = {};
      if (e?.data?.errors) {
        for (const err of e.data.errors) {
          // напр. fromDate => Start date, toDate => End date
          if (err.field === "fromDate") errFields.startYear = "Неправильна дата";
          if (err.field === "toDate") errFields.endYear = "Неправильна дата";
          // Можеш розширити по потребі
        }
      }
      setFieldErrors(errFields);
    }
  }

  // Видалення
  const handleDeleteClick = async (idx: number) => {
    console.log("here is - idx:");
    console.log(idx);
    if (!uid) return
    const education = educations[idx]
    if (!education.uid) return
    try {
      await deleteEducation({ uid: education.uid }).unwrap()
      const updated = educations.filter((_, i) => i !== idx)
      setEducations(updated)
      setState(updated.length ? 'list' : 'empty')
    } catch (e) {
      // todo: error toast
      console.error('Failed to delete education', e)
    }
  }

  // Додаємо новий
  const handleAddClick = () => {
    setForm(defaultEducation)
    setEditIndex(null)
    setState('add')
  }

  // Почати редагування
  const handleEditClick = (idx: number) => {
    setForm(educations[idx])
    setEditIndex(idx)
    setState('edit')
  }

  // Зміни форми
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  // Відміна
  const handleCancel = () => {
    setState(educations.length ? 'list' : 'empty')
    setEditIndex(null)
    setForm(defaultEducation)
  }

  useEffect(() => {
    setEducations((initData?.data?.education || []).map(mapApiEducation));
    setState((initData?.data?.education?.length ?? 0) === 0 ? 'empty' : 'list');
  }, [initData])

  // ---- UI ----
  return (

    <div className="EducationStep">
      {state === 'empty' && (
        <>
          <div className="edu-empty">
            <div className="edu-empty-icon">
              <EducationIcon />
            </div>
            <div className="edu-empty-content">
              <div className="edu-empty-title">No education added yet</div>
              <div className="edu-empty-actions">
                <button className="add-edu-btn" type="button" onClick={handleAddClick}>+ Add education</button>
                <div className="no-edu-toggle" onClick={() => setNoEducation(v => !v)}>
                  <div className={`toggle-switch${noEducation ? ' checked' : ''}`}>
                    <div className="toggle-thumb" />
                  </div>
                  <span>No education</span>
                </div>
              </div>
            </div>
          </div>
          <div className="row right">
            <button type="button" disabled={!noEducation} className="next-btn">Next</button>
          </div>
        </>
      )}

      {(state === 'add' || state === 'edit') && (
        <div className="edu-form">
          <div className="edu-form-header">
            <span>{state === 'edit' ? 'Edit education' : 'Add education'}</span>
            <button className="close-btn" onClick={handleCancel}>×</button>
          </div>
          <div className="">
            <div className="field">
              <label>Educational Institution*</label>
              <input
                className={fieldErrors.institution ? 'has-error' : ''}
                name="institution" value={form.institution} onChange={handleFormChange} placeholder="Your school, university, etc" />
            </div>
          </div>
          <div className="">
            <div className="field">
              <label>Degree</label>
              <input className={fieldErrors.institution ? 'has-error' : ''}
                name="degree" value={form.degree} onChange={handleFormChange} placeholder="Bachelor, Master, etc" />
            </div>
          </div>
          <div className=" ">
            <div className="field">
              <label>Field of study</label>
              <input className={fieldErrors.institution ? 'has-error' : ''}
                name="fieldOfStudy" value={form.fieldOfStudy} onChange={handleFormChange} placeholder="E.g. Computer Science" />
            </div>
          </div>
          <div className="">
            <div className="field">
              <label>Location</label>
              <input
                className={fieldErrors.institution ? 'has-error' : ''}
                name="location" value={form.location} onChange={handleFormChange} placeholder="City, Country" />
            </div>
          </div>
          <div className="">
            <div className="field">
              <label>Start date*</label>
              <div className="dob-input">
                <select
                  className={`month-select${fieldErrors.startMonth ? ' has-error' : ''}`} name="startMonth" value={form.startMonth} onChange={handleFormChange}>
                  <option value="">Month</option>
                  {months.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select name="startYear" className={`year-select${fieldErrors.startMonth ? ' has-error' : ''}`} value={form.startYear} onChange={handleFormChange}>
                  <option value="">Year</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {fieldErrors.startYear && (
                <div className="field-error">{fieldErrors.startYear}</div>
              )}
            </div>
          </div>
          <div className="">
            <div className="field">
              <label>End date*</label>
              <div className="dob-input">
                <select name="endMonth" className={`month-select${fieldErrors.startMonth ? ' has-error' : ''}`} value={form.endMonth} onChange={handleFormChange}>
                  <option value="">Month</option>
                  {months.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select name="endYear" className={`year-select${fieldErrors.startMonth ? ' has-error' : ''}`} value={form.endYear} onChange={handleFormChange}>
                  <option value="">Year</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {fieldErrors.endYear && (
                <div className="field-error">{fieldErrors.endYear}</div>
              )}
            </div>
          </div>
          <div className="">
            <div className="field full">
              <label>Description</label>
              <input
                className={fieldErrors.institution ? 'has-error' : ''} name="description" value={form.description} onChange={handleFormChange} placeholder="Description" />
            </div>
          </div>
          <div className="row actions">
            <button type="button" className="cancel-btn" onClick={handleCancel}>Cancel</button>
            <button type="button" className="add-btn"
              onClick={handleFormSubmit}
              disabled={isPostLoading || isPutLoading}
            >
              {state === 'edit' ? 'Save' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {state === 'list' && (
        <div>
          <div className="edu-list edu-card">
            {educations.map((edu, i) => (
              <div className="edu-block" key={edu.uid || i}>
                <div className="edu-card-content">
                  <div className="edu-main">
                    <div className="edu-title">{edu.institution}</div>
                    <div className="edu-degree">{edu.degree} {edu.fieldOfStudy && `• ${edu.fieldOfStudy}`}</div>
                    <div className="edu-date">{edu.startMonth}.{edu.startYear} – {edu.endMonth}.{edu.endYear}</div>
                    <div className="edu-location">{edu.location}</div>
                    {edu.description && <div className="edu-description">{edu.description}</div>}
                  </div>
                  <div className="edu-actions">
                    <button className="icon-btn" onClick={() => handleEditClick(i)} title="Edit"><PencilIcon /></button>
                    <button className="icon-btn" onClick={() => handleDeleteClick(i)} title="Delete" disabled={isDeleteLoading}><TrashIcon /></button>
                  </div>
                </div>
              </div>
            ))}

            <div className="add-edu-row">
              <button className="add-edu-btn" type="button" onClick={handleAddClick}>+ Add education</button>
            </div>

          </div>

          <div className="row right">
            <button type="button" className="next-btn">Next</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default EducationStep
